/**
 * useReferralShareAdapter — app-coupled props for the DS `<ReferralModal>` / `<ReferralShare>`.
 *
 * Owns referral-code state/fetch, Clipboard, Linking share, Amplitude. Frequency via injected
 * `markAsShown`. No SnackBar / legacy frequency / `useApiStatus` — share failures use `errorMessage`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Linking } from "react-native"
import { useDispatch, useSelector } from "react-redux"
import * as amplitude from "@amplitude/analytics-react-native"
import * as Clipboard from "expo-clipboard"

import { apiIdle, apiRequest } from "../../../../data/redux/actions/api"
import { referralService } from "../../../../data/services/referralService"
import { referralShareContent } from "../../adapters/parent/Referral/referralDashboardAdapter"
import type { ReferralShareAction, ReferralShareLink } from "../../../../design-system/organisms/ReferralShare"
import type { ReferralCodePayload } from "../types/referralCode"

export type { ReferralCodePayload }

export type UseReferralShareAdapterConfig = {
    userEmail?: string | null
    triggerContext: string
    metadata?: Record<string, unknown>
    preloadedReferralCode?: ReferralCodePayload | null
    markAsShown: () => Promise<void> | void
    /**
     * Ce qu'on envoie à Amplitude quand le lien est copié. Défaut inchangé : l'événement de la
     * modale avec son contexte. Le tableau de parrainage a le sien depuis toujours
     * (`referral.link_copied`, sans propriété) — garder les deux, c'est ne couper aucune courbe.
     */
    trackCopy?: () => void
}

type ApiSlice = {
    request?: boolean
    success?: boolean
    error?: unknown
    data?: ReferralCodePayload
}

export function useReferralShareAdapter(config: UseReferralShareAdapterConfig) {
    const { userEmail = null, triggerContext, metadata, preloadedReferralCode = null, markAsShown, trackCopy } = config

    const dispatch = useDispatch()
    const [referralCode, setReferralCode] = useState<ReferralCodePayload | null>(null)
    const [loadError, setLoadError] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const handledSuccessRef = useRef(false)
    const handledErrorRef = useRef(false)
    const awaitingFetchRef = useRef(false)

    const referralReq = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) => state?.api?.getReferralCode as ApiSlice | undefined
    )
    const isLoading = !!referralReq?.request

    useEffect(() => {
        if (preloadedReferralCode && !referralCode) {
            setReferralCode(preloadedReferralCode)
            setLoadError(false)
        }
    }, [preloadedReferralCode, referralCode])

    const fetchCode = useCallback(() => {
        setLoadError(false)
        setErrorMessage(null)
        handledSuccessRef.current = false
        handledErrorRef.current = false
        awaitingFetchRef.current = true
        const params = userEmail ? { email: userEmail } : {}
        dispatch(apiRequest(referralService.getReferralCode, {}, {}, params) as never)
    }, [dispatch, userEmail])

    useEffect(() => {
        if (!referralReq?.success || handledSuccessRef.current || !awaitingFetchRef.current) return
        handledSuccessRef.current = true
        awaitingFetchRef.current = false
        setReferralCode(referralReq.data ?? null)
        setLoadError(false)
        dispatch(apiIdle(referralService.getReferralCode) as never)
    }, [dispatch, referralReq?.success, referralReq?.data])

    useEffect(() => {
        if (!referralReq?.error || handledErrorRef.current || !awaitingFetchRef.current) return
        handledErrorRef.current = true
        awaitingFetchRef.current = false
        if (!referralCode && !preloadedReferralCode) setLoadError(true)
        dispatch(apiIdle(referralService.getReferralCode) as never)
    }, [dispatch, referralReq?.error, referralCode, preloadedReferralCode])

    const onModalVisible = useCallback(() => {
        amplitude.track("referral_modal.viewed", {
            trigger_context: triggerContext,
            ...metadata,
        })
        if (!referralCode && !preloadedReferralCode && !isLoading) {
            fetchCode()
        }
    }, [fetchCode, isLoading, metadata, preloadedReferralCode, referralCode, triggerContext])

    const shareContent = referralShareContent(referralCode)

    const copyLink = useCallback(() => {
        if (!referralCode?.referee_url) return
        setErrorMessage(null)
        if (trackCopy) {
            trackCopy()
        } else {
            amplitude.track("referral_modal.link_copied", {
                trigger_context: triggerContext,
                ...metadata,
            })
        }
        void Clipboard.setStringAsync(referralCode.referee_url)
        void markAsShown()
    }, [markAsShown, metadata, referralCode?.referee_url, trackCopy, triggerContext])

    const openShare = useCallback(
        async (channel: "whatsapp" | "telegram" | "email", url: string, failMessage: string) => {
            amplitude.track(`referral.share_${channel}`)
            setErrorMessage(null)
            try {
                await Linking.openURL(url)
            } catch {
                setErrorMessage(failMessage)
            }
        },
        []
    )

    const shareActions: ReferralShareAction[] = useMemo(() => {
        if (!shareContent) return []
        return [
            {
                id: "whatsapp",
                iconName: "whatsapp",
                color: "success",
                onPress: () =>
                    void openShare(
                        "whatsapp",
                        `whatsapp://send?text=${encodeURIComponent(shareContent)}`,
                        "WhatsApp n'est pas installé"
                    ),
            },
            {
                id: "telegram",
                iconName: "telegram-plane",
                color: "phone",
                onPress: () =>
                    void openShare(
                        "telegram",
                        `tg://msg?text=${encodeURIComponent(shareContent)}`,
                        "Telegram n'est pas installé"
                    ),
            },
            {
                id: "email",
                iconName: "envelope",
                color: "primary",
                onPress: () => {
                    const subject = "Découvre Masteur !"
                    void openShare(
                        "email",
                        `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(shareContent)}`,
                        "Impossible d'ouvrir l'email"
                    )
                },
            },
        ]
    }, [openShare, shareContent])

    const link: ReferralShareLink | null = referralCode?.referee_url
        ? {
              url: referralCode.referee_url,
              rewardLabel: referralCode.commission_amount ? `${referralCode.commission_amount}€` : undefined,
          }
        : null

    const handleDismiss = useCallback(() => {
        amplitude.track("referral_modal.dismissed", {
            trigger_context: triggerContext,
            ...metadata,
        })
        void markAsShown()
    }, [markAsShown, metadata, triggerContext])

    return {
        handleDismiss,
        onModalVisible,
        modalProps: {
            loading: isLoading && !link,
            error: loadError && !link,
            errorMessage,
            link,
            shareActions,
            onCopyLink: copyLink,
            onRetry: fetchCode,
        },
    }
}
